import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { toaster } from "baseui/toast";
import { defaultInstance } from "../api/httpClient";
import { useGetEstate, useGetEstateGroup } from "../api/generated";
import {
  usePropertyAdminAuthorizedEstates,
  usePropertyAdminPendingRequests,
  useSubmitPropertyAdminAccessRequest,
  useUpdatePropertyAdminAuthorizedEstate,
  useRemovePropertyAdminAuthorizedEstate,
} from "./usePropertyAdminAPI";
import type { NewPropertyAdminAccessRequest } from "../api/propertyAdmin";

// Types for Property Admin functionality
export interface PropertyAdminEstate {
  id: number;
  estateName: string;
  estateGroupName: string;
  authorizedUntil: string;
  canCreatePasses: boolean;
  estateGroup: EstateGroupAccess;
}

export interface EstateGroupAccess {
  id: number;
  name: string;
  estateCount: number;
  isAuthorized: boolean;
}

export interface EstateRequest {
  id: number;
  estateName: string;
  estateGroupName: string;
  requestedDuration: string;
  status: 'pending' | 'approved' | 'rejected';
  requestedAt: string;
  reviewedAt?: string;
  reviewedBy?: string;
  estateId: number;
  estateGroupId: number;
}

export interface NewAccessRequest {
  estateId: number;
  duration: string;
  reason: string;
}

// Mock API functions - replace with actual API calls
const mockGetAuthorizedEstates = async (): Promise<PropertyAdminEstate[]> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  return [
    {
      id: 1,
      estateName: 'Downtown Tower A - Unit 101',
      estateGroupName: 'Downtown Residences',
      authorizedUntil: '2024-12-31',
      canCreatePasses: true,
      estateGroup: { id: 1, name: 'Downtown Residences', estateCount: 15, isAuthorized: true }
    },
    {
      id: 2,
      estateName: 'Downtown Tower A - Unit 205',
      estateGroupName: 'Downtown Residences',
      authorizedUntil: '2025-01-15',
      canCreatePasses: true,
      estateGroup: { id: 1, name: 'Downtown Residences', estateCount: 15, isAuthorized: true }
    },
    {
      id: 3,
      estateName: 'Beachfront Villa 3B',
      estateGroupName: 'Beachfront Properties',
      authorizedUntil: '2024-10-15',
      canCreatePasses: true,
      estateGroup: { id: 2, name: 'Beachfront Properties', estateCount: 8, isAuthorized: true }
    },
    {
      id: 4,
      estateName: 'Beachfront Villa 5A',
      estateGroupName: 'Beachfront Properties',
      authorizedUntil: '2024-08-20',
      canCreatePasses: false,
      estateGroup: { id: 2, name: 'Beachfront Properties', estateCount: 8, isAuthorized: false }
    },
    {
      id: 5,
      estateName: 'Austin Heights Unit 12',
      estateGroupName: 'Austin Heights',
      authorizedUntil: '2024-11-30',
      canCreatePasses: false,
      estateGroup: { id: 3, name: 'Austin Heights', estateCount: 12, isAuthorized: false }
    },
    {
      id: 6,
      estateName: 'Miami Beach Condo 5A',
      estateGroupName: 'Miami Beach Residences',
      authorizedUntil: '2025-03-01',
      canCreatePasses: true,
      estateGroup: { id: 4, name: 'Miami Beach Residences', estateCount: 20, isAuthorized: true }
    }
  ];
};

const mockGetPendingRequests = async (): Promise<EstateRequest[]> => {
  await new Promise(resolve => setTimeout(resolve, 500));
  
  return [
    {
      id: 1,
      estateName: 'NYC Downtown Loft 2C',
      estateGroupName: 'NYC Downtown Lofts',
      requestedDuration: '3 months',
      status: 'pending',
      requestedAt: '2024-01-20',
      estateId: 7,
      estateGroupId: 1,
    },
    {
      id: 2,
      estateName: 'Denver Mountain View 7D',
      estateGroupName: 'Denver Mountain Views',
      requestedDuration: '1 year',
      status: 'pending',
      requestedAt: '2024-01-18',
      estateId: 9,
      estateGroupId: 2,
    },
    {
      id: 3,
      estateName: 'Seattle Waterfront 1A',
      estateGroupName: 'Seattle Waterfront',
      requestedDuration: '6 months',
      status: 'approved',
      requestedAt: '2024-01-10',
      reviewedAt: '2024-01-12',
      reviewedBy: 'Tenant Admin',
      estateId: 11,
      estateGroupId: 3,
    }
  ];
};

const mockGetAvailableEstates = async (): Promise<Array<{ id: number; name: string; groupName: string }>> => {
  await new Promise(resolve => setTimeout(resolve, 300));
  
  return [
    { id: 7, name: 'NYC Downtown Loft 2C', groupName: 'NYC Downtown Lofts' },
    { id: 8, name: 'NYC Downtown Loft 3D', groupName: 'NYC Downtown Lofts' },
    { id: 9, name: 'Denver Mountain View 7D', groupName: 'Denver Mountain Views' },
    { id: 10, name: 'Denver Mountain View 8E', groupName: 'Denver Mountain Views' },
    { id: 11, name: 'Seattle Waterfront 1A', groupName: 'Seattle Waterfront' },
    { id: 12, name: 'Seattle Waterfront 2B', groupName: 'Seattle Waterfront' },
    { id: 13, name: 'Portland Garden 3B', groupName: 'Portland Gardens' },
    { id: 14, name: 'Portland Garden 4C', groupName: 'Portland Gardens' },
    { id: 15, name: 'Chicago Downtown 1A', groupName: 'Chicago Downtown' },
    { id: 16, name: 'Chicago Downtown 2B', groupName: 'Chicago Downtown' },
  ];
};

const mockSubmitAccessRequest = async (request: NewAccessRequest): Promise<EstateRequest> => {
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  const availableEstates = await mockGetAvailableEstates();
  const selectedEstate = availableEstates.find(e => e.id === request.estateId);
  
  if (!selectedEstate) {
    throw new Error('Estate not found');
  }
  
  return {
    id: Date.now(),
    estateName: selectedEstate.name,
    estateGroupName: selectedEstate.groupName,
    requestedDuration: request.duration,
    status: 'pending',
    requestedAt: new Date().toISOString().split('T')[0],
    estateId: request.estateId,
    estateGroupId: 0, // This would be set based on the estate group selection
  };
};

// Custom hook for Property Admin functionality
export const usePropertyAdmin = (userId?: number | null) => {
  const queryClient = useQueryClient();

  const userIdParam = userId ? { user_id: userId } : undefined;

  // Get authorized estates from real API — scoped to the current user
  const {
    authorizedEstates = [],
    isLoading: isLoadingAuthorizedEstates,
    error: errorAuthorizedEstates,
    totalPages: authorizedEstatesTotalPages,
    currentPage: authorizedEstatesCurrentPage,
    totalElements: authorizedEstatesTotalElements,
  } = usePropertyAdminAuthorizedEstates(userIdParam);

  // Get pending requests from real API — scoped to the current user
  const {
    pendingRequests = [],
    isLoading: isLoadingPendingRequests,
    error: errorPendingRequests,
    totalPages: pendingRequestsTotalPages,
    currentPage: pendingRequestsCurrentPage,
    totalElements: pendingRequestsTotalElements,
  } = usePropertyAdminPendingRequests(userIdParam);

  // Get estate groups for dropdown
  const {
    data: estateGroupsResponse,
    isLoading: isLoadingEstateGroups,
    error: errorEstateGroups,
  } = useGetEstateGroup({
    page: 1,
    limit: 100,
  } as any);

  // Get available estates for requests
  const {
    data: estatesResponse,
    isLoading: isLoadingAvailableEstates,
    error: errorAvailableEstates,
  } = useGetEstate({
    page: 1,
    limit: 1000,
    expand: 'estate_group,estate_group_id',
  } as any);

  // Extract data from responses
  const estateGroups = (estateGroupsResponse?.data as any)?.data || estateGroupsResponse?.data || [];
  const availableEstates = Array.isArray(estatesResponse?.data) ? estatesResponse.data : [];

  // Submit new access request using real API
  const submitAccessRequestMutation = useSubmitPropertyAdminAccessRequest();
  
  // Update authorized estate using real API
  const updateAuthorizedEstateMutation = useUpdatePropertyAdminAuthorizedEstate();
  
  // Remove authorized estate using real API
  const removeAuthorizedEstateMutation = useRemovePropertyAdminAuthorizedEstate();

  // Calculate statistics
  const stats = {
    authorizedEstatesCount: authorizedEstates.length,
    totalEstateGroups: new Set(authorizedEstates.map((e) => e.estate?.estate_group_id).filter((id) => id != null)).size,
    pendingRequestsCount: pendingRequests.filter(r => r.status_id === 1).length, // 1 = pending
    activePermissionsCount: authorizedEstates.filter(e => e.status_id === 2).length, // 2 = active
  };

  return {
    // Data
    authorizedEstates,
    pendingRequests,
    availableEstates,
    estateGroups,
    
    // Loading states
    isLoadingAuthorizedEstates,
    isLoadingPendingRequests,
    isLoadingAvailableEstates,
    isLoadingEstateGroups,
    isSubmittingRequest: submitAccessRequestMutation.isPending,
    isUpdatingEstate: updateAuthorizedEstateMutation.isPending,
    isRemovingEstate: removeAuthorizedEstateMutation.isPending,
    
    // Errors
    errorAuthorizedEstates,
    errorPendingRequests,
    errorAvailableEstates,
    errorEstateGroups,
    
    // Actions
    submitAccessRequest: (data: NewPropertyAdminAccessRequest) => submitAccessRequestMutation.mutate(data),
    updateAuthorizedEstate: (id: string, data: any) => updateAuthorizedEstateMutation.mutate({ id, data }),
    removeAuthorizedEstate: (id: string) => removeAuthorizedEstateMutation.mutate(id),
    
    // Pagination data
    authorizedEstatesTotalPages,
    authorizedEstatesCurrentPage,
    authorizedEstatesTotalElements,
    pendingRequestsTotalPages,
    pendingRequestsCurrentPage,
    pendingRequestsTotalElements,
    
    // Statistics
    stats,
  };
};

// Hook for duration options
export const useDurationOptions = () => {
  return [
    { label: '1 Month', value: '1_month' },
    { label: '3 Months', value: '3_months' },
    { label: '6 Months', value: '6_months' },
    { label: '1 Year', value: '1_year' },
  ];
}; 